import { z } from 'zod';

// Customers, overseas agents and carriers, and the prices they gave. The server builds these from the Serb Mind vault.

export const SERVICES = ['FCL', 'LCL', 'Air', 'Road'] as const;
const Status = z.enum(['Active', 'Inactive']);

const Company = {
  id: z.string(),
  name: z.string(),
  country: z.string(),
  city: z.string(),
  address: z.string(),
  website: z.string(),
  contact: z.string(),
  email: z.string(),
  phone: z.string(),
  status: Status,
  /** how many days ago the first shipment or request was */
  addedDaysAgo: z.number(),
  /** what the vault note says about them: soft spots, do's and don'ts, history */
  notes: z.string(),
};

export const CustomerSchema = z.object({
  ...Company,
  industry: z.string(),
  terms: z.string(),
  clearanceBroker: z.string(),
  clearanceBrokerCustomsId: z.string(),
  taxId: z.string(),
});

export const AgentSchema = z.object({
  ...Company,
  industry: z.string(),
  services: z.array(z.enum(SERVICES)),
  coverage: z.string(),
});

export const CarrierSchema = z.object({
  ...Company,
  mode: z.enum(['Sea', 'Air', 'Road']),
  services: z.array(z.enum(SERVICES)),
  cargoTypes: z.array(z.string()),
  coverage: z.string(),
  /** how fast it usually answers. Empty until the vault records it. */
  responseTime: z.string(),
  /** other names the shipments use for it: "EK", "mearsk" */
  aliases: z.array(z.string()),
});

const Outcome = z.enum(['Selected', 'Not Selected', 'Pending', 'No Quote']);

/** A request for rates sent to an agent, and what came back */
export const RfqSchema = z.object({
  agentId: z.string(),
  srb: z.string(),
  /** "2025-07-22" */
  date: z.string(),
  /** null while no rate has been given */
  rate: z.number().nullable(),
  currency: z.enum(['USD', 'EUR', 'GBP']),
  transit: z.string(),
  outcome: Outcome,
  /** did it arrive by the promised date? Empty until the vault says. */
  delivered: z.enum(['On time', 'Late']).optional(),
});

/** A rate a carrier gave for a route */
export const CarrierRateSchema = z.object({
  carrierId: z.string(),
  date: z.string(),
  route: z.string(),
  service: z.enum(SERVICES),
  rate: z.number(),
  currency: z.enum(['USD', 'EUR']),
  per: z.enum(['kg', 'CBM', 'container']),
  /** days the rate stays valid */
  validDays: z.number(),
  outcome: z.enum(['Selected', 'Not Selected', 'Pending']),
});

export type Customer = z.infer<typeof CustomerSchema>;
export type Agent = z.infer<typeof AgentSchema>;
export type Carrier = z.infer<typeof CarrierSchema>;
export type Rfq = z.infer<typeof RfqSchema>;
export type CarrierRate = z.infer<typeof CarrierRateSchema>;
export type Service = (typeof SERVICES)[number];
